import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

const PERIODS = [{ id: "today", label: "24h" }, { id: "week", label: "7 days" }, { id: "month", label: "30 days" }, { id: "lifetime", label: "Lifetime" }];

/** AdminOwnerEarnings — what the app owner keeps: gift fees, platform share of every gift/room/sub, coin sales. */
export default function AdminOwnerEarnings() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("lifetime");

  const load = async () => {
    try { const { data } = await api.get("/admin/owner-earnings"); setD(data); }
    catch { toast.error("Could not load owner earnings"); }
  };
  useEffect(() => { load(); }, []);

  if (!d) return <p className="text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading…</p>;
  const p = d.periods[period];
  const coins = d.coin_sales[period];
  const maxDay = Math.max(1, ...d.series.map((s) => Math.abs(s.owner_net_waves)));

  return (
    <div data-testid="admin-owner-earnings" className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-cream/60 text-xs tracking-[0.25em] uppercase font-bold flex items-center gap-1.5"><Crown size={14} weight="fill" className="text-jam-gold" /> Owner earnings</p>
        <div className="flex gap-1.5">
          {PERIODS.map((x) => <button key={x.id} onClick={() => setPeriod(x.id)} data-testid={`owner-period-${x.id}`} className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase ${period === x.id ? "bg-papaya text-ink" : "bg-white/5 text-cream/60"}`}>{x.label}</button>)}
          <button onClick={load} className="text-papaya text-xs flex items-center gap-1 ml-1"><ArrowsClockwise size={12} weight="bold" /></button>
        </div>
      </div>

      <div className="rounded-3xl border border-jam-gold/40 bg-jam-gold/10 p-5" data-testid="owner-net-card">
        <p className="text-jam-gold text-[10px] tracking-[0.3em] uppercase font-black">You keep · {PERIODS.find((x) => x.id === period).label}</p>
        <p className="text-cream text-4xl sm:text-5xl font-black tracking-tighter mt-1" data-testid="owner-net-waves">{p.owner_net_waves.toLocaleString()} <span className="text-2xl">🪙</span></p>
        <p className="text-cream/60 text-xs mt-1">≈ ${p.owner_net_usd_estimate.toLocaleString()} at ${d.wave_usd_rate}/wave · {p.gross_waves.toLocaleString()} waves spent in-app − {p.paid_out_pearls.toLocaleString()} pearls paid to hosts/creators</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
          <Mini label="Coin sales (PayPal)" value={`$${coins.paypal_usd.toFixed(2)}`} />
          <Mini label="Coin sales (Play)" value={`$${coins.play_usd.toFixed(2)}`} />
          <Mini label="Pending cashouts" value={d.pending_cashouts} warn={d.pending_cashouts > 0} />
        </div>
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="owner-series">
        <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold mb-3">Last 14 days · net waves kept</p>
        <div className="flex items-end gap-1 h-24">
          {d.series.map((s) => (
            <div key={s.day} className="flex-1 flex flex-col items-center justify-end h-full" title={`${s.day}: ${s.owner_net_waves}`}>
              <div className={`w-full rounded-t ${s.owner_net_waves >= 0 ? "bg-papaya" : "bg-red-400"}`} style={{ height: `${Math.max(2, (Math.abs(s.owner_net_waves) / maxDay) * 100)}%` }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-cream/30 text-[9px] mt-1"><span>{d.series[0]?.day.slice(5)}</span><span>{d.series[d.series.length - 1]?.day.slice(5)}</span></div>
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid="owner-sources">
        <div className="flex items-center justify-between mb-2">
          <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold">Where it comes from</p>
          <Link to="/admin" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="admin-tab-revenue"]')?.click(); }} className="text-papaya text-[10px] font-black tracking-[0.15em] uppercase">Edit split · fee {d.gift_creation_fee_waves} 🪙</Link>
        </div>
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-1.5 text-xs items-center">
          <span className="text-cream/40 text-[9px] tracking-[0.2em] uppercase font-bold">Source</span>
          <span className="text-cream/40 text-[9px] tracking-[0.2em] uppercase font-bold text-right">Spent</span>
          <span className="text-cream/40 text-[9px] tracking-[0.2em] uppercase font-bold text-right">Paid out</span>
          <span className="text-jam-gold text-[9px] tracking-[0.2em] uppercase font-bold text-right">You keep</span>
          {p.sources.map((s) => (
            <React.Fragment key={s.source}>
              <span className="text-cream font-bold truncate" data-testid={`owner-source-${s.source.replace(/\W+/g, "_").toLowerCase()}`}>{s.source} <span className="text-cream/40 font-normal">× {s.count}</span></span>
              <span className="text-cream/70 text-right tabular-nums">{s.gross_waves.toLocaleString()}</span>
              <span className="text-cream/50 text-right tabular-nums">−{s.paid_out_pearls.toLocaleString()}</span>
              <span className="text-jam-gold font-black text-right tabular-nums">{s.owner_net_waves.toLocaleString()}</span>
            </React.Fragment>
          ))}
        </div>
        <p className="text-cream/40 text-[10px] mt-3">Current split: hosts {d.split.host_share_pct}% · platform {d.split.platform_share_pct}% · designers {d.split.creator_reward_pct}%. Gift designers pay {d.gift_creation_fee_waves} waves per submission (non-refundable).</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Recent title="Latest gift creation fees" items={d.recent_fees} render={(t) => `@${t.user?.username || "?"} · ${Math.abs(t.amount)} 🪙 · ${t.meta?.gift_name || t.ref}`} testid="owner-recent-fees" />
        <Recent title="Latest gift sales" items={d.recent_gift_sales} render={(t) => `@${t.user?.username || "?"} sent ${t.meta?.gift_id || "gift"} · ${Math.abs(t.amount)} 🪙`} testid="owner-recent-sales" />
      </div>
    </div>
  );
}

function Mini({ label, value, warn }) {
  return (
    <div className={`rounded-2xl p-3 border ${warn ? "bg-red-500/10 border-red-500/30" : "bg-ink/40 border-white/10"}`}>
      <p className="text-cream/50 text-[9px] tracking-[0.2em] uppercase font-bold">{label}</p>
      <p className="text-cream text-lg font-black tracking-tight">{value}</p>
    </div>
  );
}

function Recent({ title, items, render, testid }) {
  return (
    <div className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid={testid}>
      <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold mb-2">{title}</p>
      {items.length === 0 ? <p className="text-cream/40 text-xs">Nothing yet.</p> : items.slice(0, 8).map((t) => (
        <div key={t.id} className="flex justify-between gap-2 text-[11px] text-cream/80 py-1 border-b border-white/5">
          <span className="truncate">{render(t)}</span><span className="text-cream/40 flex-shrink-0">{t.created_at?.slice(5, 10)}</span>
        </div>
      ))}
    </div>
  );
}
